import { Eraser, Shuffle, Sigma, TrendingUp, BarChart3 } from "lucide-react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../../hooks/useSectionParallax";

const HIGHLIGHTS = [
  { name: "Kaplan–Meier curves", detail: "Time-to-event analysis by group, with no code." },
  { name: "Regression models", detail: "Linear, logistic, and Poisson regression with interaction terms and a configurable confidence level." },
  { name: "Table 1 comparisons", detail: "Mean ± SD or median (IQR), n (%), the right test, and optional effect sizes." },
];

// Exhaustive for what the Flow engine supports today — don't add items it doesn't.
const GROUPS = [
  { title: "Clean", icon: Eraser, lines: ["Reviewed, step-by-step repairs", "Remove rows, drop missing, de-duplicate, filter", "Rename, split, trim, fill, recode columns", "Save cleaned data as a new file"] },
  { title: "Transform", icon: Shuffle, lines: ["Filter, recode, derive columns", "Case/when logic", "Melt wide to long, sort", "Group by: sum, mean, count, min, max"] },
  { title: "Stats", icon: Sigma, lines: ["Summary statistics", "Pearson, Spearman, Kendall correlation", "Automatic EDA profile", "Outliers: IQR, z-score, modified z-score", "t-test, Mann-Whitney U, ANOVA, Kruskal-Wallis, chi-squared, Fisher's exact, and more"] },
  { title: "Model", icon: TrendingUp, lines: ["Linear, logistic, and Poisson GLMs", "Multinomial and ordinal regression", "Interaction terms", "Configurable confidence level"] },
  { title: "Charts", icon: BarChart3, lines: ["Bar, line, scatter, pie, heatmap", "Box, violin, histogram, Q–Q plot", "ROC and Kaplan–Meier curves", "Data-table preview"] },
];

function Highlights() {
  return (
    <div className="grid sm:grid-cols-3 gap-5 mb-14">
      {HIGHLIGHTS.map(({ name, detail }) => (
        <div key={name} className="rounded-2xl border border-[#0284C7]/25 dark:border-[#38BDF8]/25 bg-white dark:bg-[#0D0D10] p-6">
          <p className="text-base font-medium text-[#0A0A0F] dark:text-white mb-2">{name}</p>
          <p className="text-sm text-[#5C5C66] dark:text-[#9A9A9A]">{detail}</p>
        </div>
      ))}
    </div>
  );
}

export function FlowBlockCatalog() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <div ref={ref}>
      <motion.div style={{ y: fgY }}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0284C7] dark:text-[#38BDF8] mb-3 text-center">Flow · Blocks</p>
        <h3 className="text-3xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white text-center max-w-2xl mx-auto mb-4">
          Rigorous statistics, no code.
        </h3>
        <p className="text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A] text-center max-w-xl mx-auto mb-12">
          The real statistical methods, from summaries and hypothesis tests to regression and
          survival analysis, without writing the code.
        </p>
      </motion.div>
      <motion.div style={{ y: bgY }}>
        <Highlights />
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {GROUPS.map(({ title, icon: Icon, lines }) => (
            <div key={title} className="border-t-2 border-[#0284C7] dark:border-[#38BDF8] pt-4">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#0A0A0F] dark:text-white mb-3">
                <Icon size={15} className="text-[#0284C7] dark:text-[#38BDF8]" />{title}
              </p>
              <div className="space-y-1.5">
                {lines.map((l) => (
                  <p key={l} className="text-sm text-[#5C5C66] dark:text-[#9A9A9A]">{l}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
